// Per-member membership discounts: an admin gives one member a one-time price
// on their own membership for one parade year. Shared by the admin routes,
// checkout, the webhook and the admin UI. No Vue imports.
//
// Two kinds, matching the SubKrewe bulk discount modes:
// - 'percent': take `percentOff` off the tier price at the moment they pay.
// - 'fixed':   they pay exactly `fixedPrice` cents, whatever tier is active.
//              The final price, not an amount off.
//
// Free is deliberately not expressible: comped memberships go through Mark as
// Paid, and Stripe would refuse a checkout of just the processing fee anyway.
//
// Stored in `member_discounts/{memberId}_{year}`, one per member per year. The
// collection is written only by the Admin SDK, never by a client: member
// documents are guarded by a list of blocked fields, so a new sensitive field
// there is only safe if someone remembers to add it.

import { discountedUnitAmount, formatCents } from '../bulk/discount.js'

export const MEMBER_DISCOUNTS_COLLECTION = 'member_discounts'

/** Lowest fixed price an admin can set, in cents. Below this, use Mark as Paid. */
export const MIN_MEMBER_DISCOUNT_FIXED_PRICE = 100

export const MAX_MEMBER_DISCOUNT_REASON_LENGTH = 500

export type MemberDiscountType = 'percent' | 'fixed'

/** `revoked` is shown to admins as "Removed". */
export type MemberDiscountStatus = 'available' | 'used' | 'revoked'

export interface MemberDiscountTerms {
  type: MemberDiscountType
  /** Above 0 and below 100. Null when type is 'fixed'. */
  percentOff: number | null
  /** Cents. Null when type is 'percent'. */
  fixedPrice: number | null
}

/** A `member_discounts` document as the server writes it. */
export interface MemberDiscountRecord extends MemberDiscountTerms {
  memberId: string
  year: string
  productType: 'membership'
  reason: string
  status: MemberDiscountStatus
  createdAt: Date
  createdBy: string
  usedAt?: Date
  usedSessionId?: string
  usedOrderId?: string
  /** Cents charged per membership, and what the tier price was at the time. */
  chargedUnitAmount?: number
  regularUnitAmount?: number
  savedCents?: number
  revokedAt?: Date
  revokedBy?: string
}

export interface MemberDiscountTier {
  label: string
  unitAmount: number
}

export interface MemberDiscountRow {
  id: string
  memberId: string
  memberName: string
  year: string
  type: MemberDiscountType
  percentOff: number | null
  fixedPrice: number | null
  label: string
  reason: string
  status: MemberDiscountStatus
  createdAt: string | null
  createdBy: string | null
  createdByName: string | null
  usedAt: string | null
  usedOrderId: string | null
  chargedUnitAmount: number | null
  regularUnitAmount: number | null
  savedCents: number | null
  revokedAt: string | null
  revokedByName: string | null
  /** Cents the member would pay at the current tier; null without a product. */
  unitAmountToday: number | null
  /** Unspent, but the member already has a completed membership for the year. */
  paidWithoutIt: boolean
}

/** GET /api/admin/member-discounts */
export interface MemberDiscountsResponse {
  success: boolean
  paradeYear: string
  /** The year listed, or null when listing one member's discounts. */
  year: string | null
  /** Current membership tier per year; null when nothing is on sale. */
  tiers: Record<string, MemberDiscountTier | null>
  items: MemberDiscountRow[]
  totals: {
    available: number
    used: number
    revoked: number
    paidWithoutIt: number
    savedCents: number
  }
}

export function memberDiscountDocId(memberId: string, year: string | number): string {
  return `${memberId}_${year}`
}

function finiteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

export type MemberDiscountValidation =
  | { ok: true, terms: MemberDiscountTerms }
  | { ok: false, error: string }

/**
 * Check what an admin entered. Percentages keep up to two decimals, like the
 * bulk discount; fixed prices are whole cents.
 */
export function validateMemberDiscountTerms(input: {
  type?: unknown
  percentOff?: unknown
  fixedPrice?: unknown
}): MemberDiscountValidation {
  if (input.type === 'percent') {
    const percent = finiteNumber(input.percentOff)
    if (percent === null || percent <= 0) {
      return { ok: false, error: 'Enter a percentage above 0' }
    }
    if (percent >= 100) {
      return { ok: false, error: 'A discount must be below 100%. To give a membership for free, use Mark as Paid.' }
    }
    return { ok: true, terms: { type: 'percent', percentOff: Math.round(percent * 100) / 100, fixedPrice: null } }
  }

  if (input.type === 'fixed') {
    const fixed = finiteNumber(input.fixedPrice)
    if (fixed === null || !Number.isInteger(fixed)) {
      return { ok: false, error: 'Enter the fixed price in whole cents' }
    }
    if (fixed < MIN_MEMBER_DISCOUNT_FIXED_PRICE) {
      return {
        ok: false,
        error: `A fixed price must be at least ${formatCents(MIN_MEMBER_DISCOUNT_FIXED_PRICE)}. To give a membership for free, use Mark as Paid.`,
      }
    }
    return { ok: true, terms: { type: 'fixed', percentOff: null, fixedPrice: fixed } }
  }

  return { ok: false, error: 'Choose either a percentage off or a fixed price' }
}

/**
 * The terms of a stored discount that can still be spent, or null when it is
 * used, removed, or its fields no longer validate.
 */
export function readUsableMemberDiscount(source: Record<string, unknown> | null | undefined): MemberDiscountTerms | null {
  if (!source || source.status !== 'available') return null
  const result = validateMemberDiscountTerms(source)
  return result.ok ? result.terms : null
}

/**
 * Cents one membership costs with the discount, given the tier price. A fixed
 * price above the tier drops to the tier price, so a discount never makes a
 * membership cost more than buying it normally.
 */
export function memberDiscountUnitAmount(terms: MemberDiscountTerms, tierUnitAmount: number): number {
  return discountedUnitAmount(
    { type: terms.type, percentOff: terms.percentOff ?? 0, fixedPrice: terms.fixedPrice, cap: null },
    tierUnitAmount,
  )
}

/** True when the discount would lower what this tier price charges. */
export function memberDiscountLowersPrice(terms: MemberDiscountTerms, tierUnitAmount: number): boolean {
  return memberDiscountUnitAmount(terms, tierUnitAmount) < tierUnitAmount
}

/** Short label: "25% off" or "Fixed price $40.00". */
export function memberDiscountLabel(terms: MemberDiscountTerms): string {
  if (terms.type === 'fixed') return `Fixed price ${formatCents(terms.fixedPrice ?? 0)}`
  return `${terms.percentOff ?? 0}% off`
}

/** What each kind means, for the admin UI. */
export const MEMBER_DISCOUNT_TYPE_HELP: Record<MemberDiscountType, string> = {
  percent: 'Takes a percentage off the membership price at the moment they pay. If the price goes up before then, they get the same percentage off the higher price.',
  fixed: 'Sets the exact amount they pay for their membership, whichever price tier is active when they pay. Enter the final price, not the amount to take off.',
}

/**
 * One sentence saying what the member would pay today, for the admin UI.
 * `tierUnitAmount` is null when the year has no active membership product.
 */
export function explainMemberDiscountPrice(terms: MemberDiscountTerms, tierUnitAmount: number | null): string {
  if (tierUnitAmount === null) {
    return 'There is no membership on sale for this year yet, so the price they would pay can\'t be worked out.'
  }
  const pays = memberDiscountUnitAmount(terms, tierUnitAmount)
  if (pays >= tierUnitAmount) {
    return `That's not lower than the current price of ${formatCents(tierUnitAmount)}, so they would pay the normal price today and the discount would stay unused.`
  }
  return `At the current price of ${formatCents(tierUnitAmount)} they would pay ${formatCents(pays)}, saving ${formatCents(tierUnitAmount - pays)}, plus the card processing fee.`
}
